import {
  faChevronUp,
  faChevronDown,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useContext, useEffect, useRef } from 'react';
import Cookies from 'react-cookies';
import { useSelector } from 'react-redux';
import { VIP_IMG } from '../../../config/constants/images';
import cookies from '../../../config/cookies/cookies';
import { filterPopularity } from '../../../config/setup/setup';
import {
  FILTER_PRICE_SHORT,
  VIP_DISABLED_POPUP_DESC,
  VIP_DISABLED_POPUP_TITLE,
  VIP_ENABLED_POPUP_DESC,
  VIP_ENABLED_POPUP_TITLE,
  VIP_SEARCH_DISABLED,
  VIP_SEARCH_ENABLED,
  VIP_TOOLTIP_SEEN_KEY,
} from '../../../config/text/text';
import { trackEvent } from '../../../helpers/analytics';
import useCheckMobile from '../../../helpers/checkMobile';
import ThemeContext from '../../../providers/ThemeProvider';
import { PriceRangeNewFilter } from '../../filters/RangeSelectorNewFilter';
import CTAButton from '../button/CTAButton';
import Switch from '../switch/Switch';
import useToast from '../toast/UseToast';
import FilterMultiSelectItem from './FilterMultiSelectItem';

const FilterOptions = ({
  categories,
  count,
  vipSearchFilter,
  sortBy,
  sliderValues,
  updateFilter,
  filterData,
  priceStats,
  selectedFilters = {},
  setSelectedFilters,
  currentMarks,
  isSubCat,
  query,
  variant,
  handleSortSelection,
}) => {
  const theme = useContext(ThemeContext);
  const [showMore, setShowMore] = useState({});
  const [vipFilterTooltip, setVipFilterTooltip] = useState(false);
  const [isSortExpanded, setIsSortExpanded] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const isMobile = useCheckMobile();
  const toast = useToast();
  const isVipUser = useSelector((state) => state?.user?.userprofile?.vipUser);
  const isSdTestCookieB = Cookies.load(cookies.sdTest) === 'b';

  const getNonPriceFilterPosition = (pos) => {
    if (!filterData?.length) return null;

    const nonPriceFilters = filterData.filter(
      (filter) =>
        filter.linkText !== 'Price' &&
        filter.position !== null &&
        Array.isArray(filter.values) &&
        filter.values.length > 0,
    );

    return nonPriceFilters.length > 0 ? nonPriceFilters[pos]?.position : null;
  };

  const isPriceFilterFirst = () => {
    const firstNonPricePosition = getNonPriceFilterPosition(0);

    return filterData.some(
      (f) =>
        f.linkText === 'Price' && (f.position ?? 0) < firstNonPricePosition,
    );
  };
  const isFirstRender = useRef(true);
  const previousVipSearchFilter = useRef(vipSearchFilter);
  const isTogglingVipFilter = useRef(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const initialShowMore = {};
    filterData.forEach((filter) => {
      initialShowMore[filter.linkText] = false;
    });
    setShowMore(initialShowMore);
  }, [query]);

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters((previous) => ({
      ...previous,
      [filterType]: value,
    }));
    if (isSubCat) {
      trackEvent('newfilters_applied_subCat');
    }
  };
  useEffect(() => {
    if (!isMobile) {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        previousVipSearchFilter.current = vipSearchFilter;

        return;
      }

      if (previousVipSearchFilter.current !== vipSearchFilter) {
        if (isVipUser && !isSubCat) {
          const message = vipSearchFilter
            ? VIP_SEARCH_ENABLED
            : VIP_SEARCH_DISABLED;
          toast.addToast(message, 'toast-success');
        }
        previousVipSearchFilter.current = vipSearchFilter;
      }
    } else {
      if (isFirstRender.current) {
        isFirstRender.current = false;

        return;
      }

      if (isTogglingVipFilter.current) {
        isTogglingVipFilter.current = false;

        return;
      }
      // If VIP search filter is changed, show toast
      if (isVipUser && !isSubCat) {
        toast.addToast(
          vipSearchFilter ? VIP_SEARCH_ENABLED : VIP_SEARCH_DISABLED,
          'toast-success',
        );
      }
    }
  }, [vipSearchFilter]);

  useEffect(() => {
    // Check if user has already seen the VIP tooltip in this session
    const hasSeenVipTooltip = sessionStorage.getItem(VIP_TOOLTIP_SEEN_KEY);

    if (!hasSeenVipTooltip) {
      // Show tooltip for first-time users in this session
      const showTooltipTimeout = setTimeout(
        () => setVipFilterTooltip(true),
        2_000,
      );

      const hideTooltipTimeout = setTimeout(() => {
        setVipFilterTooltip(false);
        // Set sessionStorage when tooltip is automatically hidden
        sessionStorage.setItem(VIP_TOOLTIP_SEEN_KEY, 'true');
      }, 17_000);

      return () => {
        clearTimeout(hideTooltipTimeout);
        clearTimeout(showTooltipTimeout);
      };
    }
  }, []);

  const toggleVipFilter = () => {
    const updatedVipFilter = !vipSearchFilter;
    isTogglingVipFilter.current = true;

    updateFilter(
      {},
      true, // preserveExistingFilters
      updatedVipFilter, // Explicit VIP filter value
    );
    setVipFilterTooltip(false);
  };

  const handleTooltipClose = () => {
    setVipFilterTooltip(false);
    // Set sessionStorage when user manually closes the tooltip
    sessionStorage.setItem(VIP_TOOLTIP_SEEN_KEY, 'true');
  };

  const handleShowMore = (filterType) => {
    setShowMore((previous) => ({
      ...previous,
      [filterType]: !previous[filterType],
    }));
  };

  return (
    <div className="filter-options-container">
      {isMounted && !isMobile && (
        <div className="filters-header">
          <h2>Filters</h2>
          {count > 0 && (
            <span className="count-results">{`(${count} ${
              count > 1 ? 'Results' : 'Result'
            })`}</span>
          )}
        </div>
      )}
      <div className="filters-content">
        {isVipUser && !isSubCat && isMobile && !isSdTestCookieB && (
          <div className="filter-item--vip-switch">
            <img
              alt="VIP Logo"
              className="filter-item--vip-logo"
              src={VIP_IMG[process.env.NEXT_PUBLIC_BRAND]}
            />
            <Switch
              colorScheme={theme.colors.filtervipswitch}
              isOn={vipSearchFilter}
              label={'Vip Search Switch'}
              onChange={toggleVipFilter}
              size="small"
            />
            <div
              className={classNames('vip-popover', {
                'vip-popover--visible': vipFilterTooltip,
              })}
            >
              <span className="vip-popover-title">
                {vipSearchFilter
                  ? VIP_ENABLED_POPUP_TITLE
                  : VIP_DISABLED_POPUP_TITLE}
              </span>
              <p>
                {vipSearchFilter
                  ? VIP_ENABLED_POPUP_DESC
                  : VIP_DISABLED_POPUP_DESC}
              </p>
              <CTAButton
                className="secondary-button"
                onClick={handleTooltipClose}
              >
                Got it
              </CTAButton>
            </div>
          </div>
        )}
        {/* Sort section - only show when variant is dropdown_filter */}
        {variant === 'old_filter' && (
          <div className="sort-section">
            <div
              className="sort-header"
              onClick={() => setIsSortExpanded(!isSortExpanded)}
            >
              <h3>Sort By</h3>
              <FontAwesomeIcon
                icon={isSortExpanded ? faChevronUp : faChevronDown}
              />
            </div>
            {isSortExpanded && (
              <div className="sort-options">
                {filterPopularity.map((option) => (
                  <button
                    className={`sort-option ${
                      sortBy === option.value ? 'selected' : ''
                    }`}
                    key={option.value}
                    onClick={() =>
                      handleSortSelection && handleSortSelection(option.value)
                    }
                    type="button"
                    value={option?.value}
                  >
                    <span>{option.name}</span>
                    {sortBy === option.value && (
                      <FontAwesomeIcon icon={faCheck} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Show price filter at top for search pages */}
        {!isSubCat && (
          <div className="price-filter-wrapper">
            <p className="heading">{FILTER_PRICE_SHORT}</p>
            <div className="filter-range-section">
              <PriceRangeNewFilter
                currentMarks={currentMarks}
                inline
                invert={false}
                isNewFilterApplied
                isSubCat={isSubCat}
                priceStats={priceStats}
                sliderValues={sliderValues}
                sortBy={sortBy}
                subCat={categories}
                updateFilter={updateFilter}
                vipSearchFilter={vipSearchFilter}
              />
            </div>
          </div>
        )}
        {filterData?.map((filter) => {
          // Handle Price filter for subcat pages based on API position
          if (filter.linkText === 'Price' && isSubCat) {
            return (
              <div className="price-filter-wrapper" key="price-filter">
                {filterData?.length !== 1 && !isPriceFilterFirst() && (
                  <div className="line" />
                )}
                <p className="heading">{FILTER_PRICE_SHORT}</p>
                <div className="filter-range-section">
                  <PriceRangeNewFilter
                    currentMarks={currentMarks}
                    inline
                    invert={false}
                    isNewFilterApplied
                    isSubCat={isSubCat}
                    priceStats={priceStats}
                    sliderValues={sliderValues}
                    sortBy={sortBy}
                    subCat={categories}
                    updateFilter={updateFilter}
                    vipSearchFilter={vipSearchFilter}
                  />
                </div>
              </div>
            );
          }

          if (!filter.values || filter.values.length === 0) return null;

          return (
            <FilterSectionWithMultiSelect
              firstNonPricePosition={getNonPriceFilterPosition(0)}
              isPriceFilterFirst={isPriceFilterFirst()}
              isSubCat={isSubCat}
              key={filter.linkText}
              onChange={(value) =>
                handleFilterChange(filter.filterField, value)
              }
              onShowMore={handleShowMore}
              options={filter.values}
              position={filter?.position}
              secondNonPricePosition={getNonPriceFilterPosition(1)}
              selectedValues={selectedFilters[filter.filterField] || []}
              showMore={showMore[filter.linkText] || false}
              title={filter.linkText}
              type={filter.linkText}
            />
          );
        })}
      </div>
      <style jsx>{`
        .filter-options-container {
          width: 100%;
          display: flex;
          flex-direction: column;
          height: 100%;
          margin: 0 auto;
          position: relative;
        }
        .sort-section {
          position: relative;
          border-bottom: 1px solid ${theme.colors.primary};
          padding: 7px 0px;
        }
        .sort-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          padding: 10px 0;
        }
        .sort-header h3 {
          margin: 0;
          font-weight: 600;
          color: #333;
          display: flex;
        }
        .sort-options {
          display: flex;
          flex-direction: column;
          padding: 2px 0;
        }
        .sort-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 8px;
          background: none;
          cursor: pointer;
          color: #333;
        }
        .sort-option.selected {
          color: ${theme.colors.primary};
          font-weight: 600;
        }
        .filter-item--vip-switch {
          display: flex;
          justify-content: space-between;
          margin-left: -10px;
        }
        .filter-item--vip-logo {
          margin-left: 0px;
          width: 65px;
        }
        .filters-header {
          display: flex;
          background-color: ${isSdTestCookieB
            ? theme.colors.primary
            : theme.colors.white};
          justify-content: space-between;
          padding: 10px 10px 10px 15px;
          border-top: 1px solid ${theme.colors.primary};
          border-bottom: 1px solid ${theme.colors.primary};
        }
        .filters-header h2 {
          margin: 0;
          font-size: 20px;
          color: ${isSdTestCookieB ? theme.colors.white : theme.colors.primary};
        }
        .heading {
          font-size: 16px;
          font-weight: 600;
          margin: 18px 0;
          text-align: left;
        }
        .filters-content {
          flex: 1;
          overflow-y: auto;
          padding: ${isVipUser && !isSubCat ? '15px' : '0px'} 15px
            ${filterData?.length > 0 ? '0' : '20px'} 20px;
        }
        .price-filter-wrapper {
          padding-bottom: 20px;
          border-bottom: 1px solid ${theme.colors.primary};
        }
        .line {
          border-top: 1px solid ${theme.colors.primary};
        }
        .filter-range-section {
          margin-top: 15px;
          padding: 0 15px 30px 10px;
        }
        .count-results {
          margin-top: 5px;
          color: ${isSdTestCookieB ? theme.colors.white : theme.colors.primary};
        }
        .vip-popover {
          visibility: hidden;
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 5px;
          color: black;
          width: 220px;
          font-size: 14px;
          z-index: 1000;
          top: 110px;
          left: 45px;
          border: 1px solid ${theme.colors.primary};
          border-radius: 15px;
          background: white;
        }
        .vip-popover:after,
        .vip-popover:before {
          content: ' ';
          position: absolute;
          width: 0;
          height: 0;
          right: 4%;
          top: -39px;
          border: 20px solid transparent;
        }

        .vip-popover:before {
          margin-top: -1.5px;
        }
        .vip-popover:after {
          top: -11px;
          right: 16px;
          height: 20px;
          background: white;
          transform: rotate(45deg);
          border-top: 2px solid ${theme.colors.primary};
          border-left: 2px solid ${theme.colors.primary};
        }

        .vip-popover-title {
          margin-top: 2px;
          font-size: 18px;
          font-weight: 600;
        }

        .vip-popover--visible {
          visibility: visible;
        }
        /* Medium Tablet - mdUp to lgDown */
        @media (min-width: ${theme.breakpoints.mdUp}) and (max-width: ${theme
            .breakpoints.lgDown}) {
          .filter-options-container {
            min-height: 500px;
            border-radius: 12px;
            margin: 0 0;
            padding: 0 20px 20px 20px;
          }
          .heading {
            font-size: 18px;
            margin: 22px 0 14px 0;
          }
          .filters-content {
            padding: 24px;
            padding-bottom: 0px;
          }
          .filter-range-section {
            margin-top: 20px;
            padding: 0 20px 40px 16px;
          }
        }

        /* Small Tablet - smUp to mdDown (576px - 767.98px) */
        @media (min-width: ${theme.breakpoints.smUp}) and (max-width: ${theme
            .breakpoints.mdDown}) {
          .filter-options-container {
            max-width: 380px;
            min-height: 480px;
            border-radius: 10px;
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
            margin: 0 0;
          }
          .heading {
            font-size: 17px;
            margin: 20px 0 12px 0;
          }
          .filters-content {
            padding: 20px;
            padding-bottom: 0px;
          }
          .filter-range-section {
            margin-top: 18px;
            padding: 0 18px 35px 14px;
          }
        }
        /* Mobile - below smUp */
        @media (max-width: ${theme.breakpoints.smDown}) {
          .filter-options-container {
            width: 100%;
            height: max-content;
            border-radius: 0;
            box-shadow: none;
            margin: 0;
            margin-top: -15px;
          }
          .heading {
            font-size: 16px;
            margin: 18px 0 10px 0;
          }
          .filters-content {
            padding: 18px;
            padding-bottom: 20px;
            overflow-x: hidden;
          }
          .filter-item--vip-switch {
            margin-left: -4px;
          }
          .filter-range-section {
            margin-top: 16px;
            padding: 0 30px 30px 18px;
          }
          .vip-popover {
            top: 64px;
            left: 45%;
            width: 50%;
          }
          .filter-item--vip-logo {
            margin-left: 2px;
          }
          .sort-option {
            padding: 6px 3px;
            font-size: 14px;
            border: none;
          }
          .sort-header {
            padding-top: 15px;
          }
          .sort-header h3 {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
};

const FilterSectionWithMultiSelect = ({
  title,
  options,
  selectedValues,
  onChange,
  showMore,
  onShowMore,
  type,
  isMapPage = false,
  inline = false,
  invert = false,
  position = null,
  isSubCat = false,
  firstNonPricePosition = null,
  isPriceFilterFirst = false,
  secondNonPricePosition = null,
}) => {
  const hasSelectedValues =
    Array.isArray(selectedValues) && selectedValues.length > 0;

  const [isExpanded, setIsExpanded] = useState(() =>
    isSubCat
      ? position === firstNonPricePosition || hasSelectedValues
      : position === secondNonPricePosition || hasSelectedValues,
  );

  const hideBorder = () => {
    return !isPriceFilterFirst && position === firstNonPricePosition;
  };

  useEffect(() => {
    if (hasSelectedValues && !isExpanded) {
      setIsExpanded(true);
    }
  }, [hasSelectedValues]);

  const theme = useContext(ThemeContext);

  const displayOptions = showMore ? options : options?.slice(0, 5);
  const hasMore = options?.length > 5;

  const handleItemSelect = (optionName) => {
    // Ensure selectedValues is always treated as an array
    const currentValues = Array.isArray(selectedValues) ? selectedValues : [];

    const newSelected = currentValues.includes(optionName)
      ? currentValues.filter((name) => name !== optionName)
      : [...currentValues, optionName];

    onChange(newSelected);
  };

  return (
    <div className="filter-section">
      {!hideBorder() && <div className="line" />}
      <div className="filter-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h3>{title}</h3>
        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
      </div>

      {isExpanded && (
        <div className="filter-options">
          <ul className="filter-options-list">
            {displayOptions?.map((option) => (
              <div className="filter-item-wrapper" key={option.name}>
                <FilterMultiSelectItem
                  inline={inline}
                  invert={invert}
                  isMapPage={isMapPage}
                  isSelected={selectedValues?.includes(option.name)}
                  item={{
                    count: option.count,
                    name: option.name,
                    value: option.name,
                  }}
                  onSelect={handleItemSelect}
                />
                <span className="option-count">({option.count})</span>
              </div>
            ))}
          </ul>

          {hasMore && (
            <button className="show-more-btn" onClick={() => onShowMore(type)}>
              {showMore ? 'Show less' : 'Show more'}
            </button>
          )}
          {!hasMore && <div className="spacer" />}
        </div>
      )}

      <style jsx>{`
        .filter-section {
          padding-bottom: 0;
          margin-bottom: 0;
          position: relative;
        }
        .line {
          border-bottom: 1px solid ${theme.colors.primary};
          margin: 0;
        }
        .filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          padding: 16px 0;
          transition: all 0.2s ease;
          border-radius: 6px;
        }
        .filter-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
          line-height: 1.2;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .filter-options {
          padding-left: 0;
        }
        .spacer {
          margin-bottom: 15px;
        }
        .filter-options-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .filter-item-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 3px 0;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        .option-count {
          font-size: 12px;
          color: #888;
          margin-left: 8px;
          font-weight: 500;
        }
        .show-more-btn {
          background: none;
          border: none;
          color: ${theme.colors.primary};
          font-size: 14px;
          cursor: pointer;
          padding: 8px 0;
          text-align: left;
          margin-top: 8px;
          font-weight: 500;
          text-decoration: underline;
          transition: all 0.2s ease;
        }
        @media (min-width: ${theme.breakpoints.mdUp}) and (max-width: ${theme
            .breakpoints.lgDown}) {
          .filter-section {
            padding-bottom: 20px;
            margin-bottom: 10px;
          }
          .filter-header {
            padding: 12px 0;
          }
          .filter-header h3 {
            font-size: 18px;
          }
          .filter-item-wrapper {
            padding: 5px 0;
          }
          .option-count {
            font-size: 14px;
            margin-left: 12px;
          }
          .show-more-btn {
            font-size: 16px;
            padding: 12px 0;
            margin-top: 12px;
          }
        }
        @media (min-width: ${theme.breakpoints.smUp}) and (max-width: ${theme
            .breakpoints.mdDown}) {
          .filter-section {
            padding-bottom: 18px;
            margin-bottom: 8px;
          }
          .filter-header {
            padding: 10px 0;
          }
          .filter-header:hover {
            padding: 10px 10px;
          }
          .filter-header h3 {
            font-size: 17px;
          }
          .filter-item-wrapper {
            padding: 4px 0;
          }
          .option-count {
            font-size: 13px;
            margin-left: 10px;
          }
          .show-more-btn {
            font-size: 15px;
            padding: 10px 0;
            margin-top: 10px;
          }
        }

        /* Mobile - below smUp (up to 575.98px) */
        @media (max-width: ${theme.breakpoints.smDown}) {
          .filter-section {
            padding-bottom: 0px;
            margin-bottom: 0px;
          }
          .filter-header {
            padding: 20px 0;
          }
          .filter-header h3 {
            font-size: 16px;
          }
          .filter-item-wrapper {
            padding: 3px 0;
          }
          .option-count {
            font-size: 12px;
            margin-left: 8px;
          }
          .show-more-btn {
            font-size: 14px;
            padding: 9px 0;
            margin-top: 9px;
          }
        }
      `}</style>
    </div>
  );
};

FilterOptions.propTypes = {
  filterData: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number,
      filterField: PropTypes.string,
      linkText: PropTypes.string,
      position: PropTypes.number,
      shortName: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          name: PropTypes.string,
        }),
      ),
    }),
  ),
  handleSortSelection: PropTypes.func,
  isSubCat: PropTypes.bool,
  selectedFilters: PropTypes.object,
  setSelectedFilters: PropTypes.func,
  variant: PropTypes.string,
};

export default FilterOptions;
